import type { AuthRole, GetAuthenticatedUserResponse } from '@ctw/shared/api/auth-service/user';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import { getZusApiBaseUrl } from '@ctw/shared/utils/environments';
import { QUERY_KEY_CURRENT_USER_ROLE } from '@ctw/shared/utils/query-keys';

export interface CurrentUserRole {
  id: string;
  name: string;
}

// The /auth/users/me document with the role relationship + included role
// resource. Reuses the shared auth-service response shape and the `AuthRole`
// resource type so the users/me contract isn't re-declared here.
interface UserMeWithRoleResponse extends GetAuthenticatedUserResponse {
  included?: Array<AuthRole>;
}

interface UseCurrentUserRoleOptions {
  /**
   * When false, the /auth/users/me fetch is skipped entirely. Callers should
   * pass false when the role is never consulted, so
   * the request doesn't fire for users whose result would go unused.
   */
  enabled?: boolean;
}

export function useCurrentUserRole({ enabled = true }: UseCurrentUserRoleOptions = {}) {
  const { data: role, isLoading } = useCtwQuery({
    queryId: QUERY_KEY_CURRENT_USER_ROLE,
    cacheAcrossBuilders: true,
    enabled,
    queryFn: async ({ requestContext, ctwFetch }) => {
      const url = new URL('/auth/users/me', getZusApiBaseUrl(requestContext.env));
      url.searchParams.set('include', 'auth/roles');

      const response = await ctwFetch<UserMeWithRoleResponse>(url.toString(), {
        headers: {
          Authorization: `Bearer ${requestContext.authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return null;
      }

      const json = response.data;
      const roleRef = json.data.relationships['auth/roles'];
      if (!roleRef) {
        return null;
      }

      const included = json.included?.find((inc) => inc.id === roleRef.data.id);
      if (!included) {
        return null;
      }

      return { id: roleRef.data.id, name: included.attributes.name } satisfies CurrentUserRole;
    },
  });

  return { role, isLoading };
}
